import { useQuery } from '@tanstack/react-query';
import { integrations } from '@cwos/api-client';
import { importMayBeBehind } from '../settings/integrationsLogic.js';

/**
 * provenance.ts (PH-UX-002) — resolves the facts `services.ts` already sends
 * about a scheduled/delivered visit (`source`, `integrationId`,
 * `vendorAbsentSince`, `lastSyncedAt`) into what `Visits.tsx` renders.
 *
 * `Visits.tsx` read none of these before this file existed — the API sent
 * them, the worker app honoured them (`now-next.ts`), Admin did not. See
 * `docs/audit/findings/PH-UX-002.md`.
 *
 * WHY `integrationId` RESOLVES THROUGH `connectors()` AND NOT A NEW READ. The
 * map this needs (installation id -> display name, needsAttention) is exactly
 * `getConnectorsOverview`'s existing shape. The query key
 * `['integrations', 'connectors']` matches `RecordsSettings.tsx`'s own key
 * (not `dataHealth.ts`'s `[...,'data-health-gap']` variant, which computes a
 * different, coarser answer) — a reader who has that screen open never pays
 * for this read twice, and one who has not still gets it, gated on the same
 * key `dataHealth.ts` already uses for the equivalent optional cross-check.
 */

export interface ConnectorLookupEntry {
  displayName: string;
  needsAttention: boolean;
}

export type ConnectorLookup = ReadonlyMap<string, ConnectorLookupEntry>;

const EMPTY_LOOKUP: ConnectorLookup = new Map();

/**
 * `enabled` must be the caller's own permission check — this fires the same
 * request `dataHealth.ts` fires for the same reason: an optional cross-check
 * a reader cannot hold the key for is never sent at all. Unlike
 * `useDataHealthGap`, this returns the lookup MAP (every row on this screen
 * needs its own answer, not one screen-wide caveat), so a caller who cannot
 * read connectors, or whose read has not resolved yet, gets an EMPTY map —
 * never a guess — and every row renders as if nothing were known about its
 * connector, which is exactly true.
 */
export function useConnectorLookup(enabled: boolean): ConnectorLookup {
  const query = useQuery({
    queryKey: ['integrations', 'connectors'],
    queryFn: () => integrations.connectors(),
    enabled,
    staleTime: 60_000,
  });
  if (!query.data) return EMPTY_LOOKUP;
  return connectorLookupFrom(query.data);
}

/** The lookup itself, pure — `useConnectorLookup` minus the read. */
export function connectorLookupFrom(
  connectors: readonly integrations.ConnectorOverview[],
): ConnectorLookup {
  const map = new Map<string, ConnectorLookupEntry>();
  for (const c of connectors) {
    if (c.instance === null) continue;
    map.set(c.instance.id, {
      displayName: c.displayName,
      // "may be behind" is about the import, not open alerts (CORE-AUD-220).
      needsAttention: importMayBeBehind(c.instance),
    });
  }
  return map;
}

/** What a single record says about where it came from. */
export type RowProvenance =
  | { kind: 'native' }
  | {
      kind: 'imported';
      /** Resolved connector name, or null when the installation is unresolved. */
      connectorName: string | null;
      vendorAbsentSince: string | null;
      /** Already ISO — the caller (`Visits.tsx`) formats it with its own clock helpers. */
      lastSyncedAt: string | null;
    };

/**
 * A PLANNED visit (`ScheduledService`) carries the full envelope. `source`
 * is the only gate — `'imported'` is the one value that says anything at all
 * (`Origin`'s own rule, restated here so the two cannot drift).
 */
export function scheduledServiceProvenance(
  row: {
    source: string;
    integrationId: string | null;
    vendorAbsentSince: string | null;
    lastSyncedAt: string | null;
  },
  lookup: ConnectorLookup,
): RowProvenance {
  if (row.source !== 'imported') return { kind: 'native' };
  const entry = row.integrationId ? lookup.get(row.integrationId) : undefined;
  return {
    kind: 'imported',
    connectorName: entry?.displayName ?? null,
    vendorAbsentSince: row.vendorAbsentSince,
    lastSyncedAt: row.lastSyncedAt,
  };
}

/**
 * A DELIVERED visit (`ServiceEvent`) now carries `integrationId` and
 * `lastSyncedAt` as well as `source` (PH-UX-002) — `service_event` has held
 * both columns since migrations 1755100000000 and 1758040000000, and the read
 * projection simply dropped them.
 *
 * `vendorAbsentSince` STAYS NULL, and that is a data fact rather than an
 * omission: `service_event` has no such column. Only a PLANNED visit
 * (`scheduled_service`) records when the vendor stopped listing it, because
 * only a planned visit can stop being listed — a delivered one already
 * happened. A delivered row therefore never renders the vendor-absent
 * sentence, and nothing here fabricates one.
 */
export function serviceEventProvenance(
  row: { source: string; integrationId: string | null; lastSyncedAt: string | null },
  lookup: ConnectorLookup,
): RowProvenance {
  if (row.source !== 'imported') return { kind: 'native' };
  return {
    kind: 'imported',
    connectorName:
      row.integrationId === null ? null : (lookup.get(row.integrationId)?.displayName ?? null),
    vendorAbsentSince: null,
    lastSyncedAt: row.lastSyncedAt,
  };
}

/**
 * Distinct, degraded connector display names actually referenced by imported
 * rows CURRENTLY ON SCREEN — never "some connector somewhere is degraded".
 * Feeds `DegradedSourceNote`, which renders nothing for an empty array.
 */
export function degradedConnectorNames(
  rows: readonly { source: string; integrationId: string | null }[],
  lookup: ConnectorLookup,
): string[] {
  const names = new Set<string>();
  for (const row of rows) {
    if (row.source !== 'imported' || row.integrationId === null) continue;
    const entry = lookup.get(row.integrationId);
    if (entry?.needsAttention) names.add(entry.displayName);
  }
  return [...names];
}
